import { useTranslation } from 'react-i18next';
import { useParams, useOutletContext } from 'react-router-dom';
import { Loader2, Plus } from 'lucide-react';

import { usePageMeta } from '@/hooks/usePageMeta';
import PageSkeleton from '@/layout/Skeleton';
import type { Project } from '@/graphql/graphql';

import { AnalysisMenuPopover } from '@/components/analyses/AnalysisMenuPopover';
import { AnalysisRunList } from '@/components/analyses/AnalysisRunList';
import { Button } from '@/components/ui/button';
import { ANALYSES } from '@/lib/analyses/registry';
import { useAnalysisAvailability } from '@/lib/analyses/useAnalysisAvailability';
import { useCanRunAnalyses } from '@/lib/analyses/useCanRunAnalyses';
import { useProjectLayers } from '@/lib/analyses/useProjectLayers';
import { DataCenterLayout } from '../DataCenterLayout';

export function AnalysesPage() {
  const { t } = useTranslation('project');
  const { projectId } = useParams<{ projectId: string }>();

  const { project, projectLoading, href } = useOutletContext<{
    project: Project;
    projectLoading: boolean;
    projectError: Error | null;
    href: string;
  }>();

  usePageMeta({ title: [t('analyses.tabLabel'), project?.name] });

  // I2: this page does not mount the viewer's Layers panel, so it loads the
  // project's layers itself. Until they land, the global atom may hold another
  // project's layers — render nothing that reads them.
  const { ready: layersReady, isError: layersError, refetch: refetchLayers } = useProjectLayers(projectId);

  /** "New analysis…" (amendment A2): opens the same anchored dropdown as the
   *  project header. Shown to editors off shared links, and only when at least
   *  one item of either mode is visible. */
  const canRun = useCanRunAnalyses();
  const availability = useAnalysisAvailability();
  const canNewAnalysis = layersReady && canRun && ANALYSES.some((d) => availability(d.id).visible);
  const newAnalysisLabel = (
    <>
      <Plus className='size-4' />
      {t('analysisLauncher.entry.newAnalysis')}
    </>
  );

  return (
    <PageSkeleton
      steps={[
        { label: projectLoading ? t('edit.loading') : (project?.name ?? ''), href: href ?? '' },
        { label: t('upload.title'), href: `${href}/datacenter` },
        {
          label: t('analyses.tabLabel'),
          href: `${href}/datacenter/analyses`,
          options: [
            { label: t('upload.tabLabel'), href: `${href}/datacenter` },
            { label: t('analyses.tabLabel'), href: `${href}/datacenter/analyses` },
          ],
        },
      ]}
    >
      <DataCenterLayout href={href ?? ''} activeTab='analyses'>
        <div className='flex min-w-0 flex-col gap-6'>
          <div className='flex flex-col items-start justify-between gap-4 sm:flex-row'>
            <div className='min-w-0'>
              <h2 data-testid='analyses-title' className='text-lg font-semibold tracking-tight'>
                {t('analyses.title')}
              </h2>
              <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>{t('analyses.subtitle')}</p>
            </div>
            {canNewAnalysis && (
              <AnalysisMenuPopover projectId={projectId!}>
                <Button data-testid='analyses-new-analysis' type='button' className='shrink-0'>
                  {newAnalysisLabel}
                </Button>
              </AnalysisMenuPopover>
            )}
          </div>
          {layersError && (
            <div
              data-testid='analyses-layers-error'
              role='alert'
              className='flex items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-300'
            >
              <span>{t('attributeTable.failedLoadLayers')}</span>
              <Button
                data-testid='analyses-layers-retry'
                type='button'
                size='sm'
                variant='outline'
                onClick={refetchLayers}
              >
                {t('stationVolumes.errors.retry')}
              </Button>
            </div>
          )}
          {layersReady ? (
            <AnalysisRunList
              projectId={projectId!}
              href={href ?? ''}
              newAnalysis={
                canNewAnalysis ? (
                  <AnalysisMenuPopover projectId={projectId!}>
                    <Button data-testid='analyses-empty-new-analysis' type='button' size='sm'>
                      {newAnalysisLabel}
                    </Button>
                  </AnalysisMenuPopover>
                ) : undefined
              }
            />
          ) : (
            <div data-testid='analyses-loading-layers' className='flex justify-center py-8'>
              <Loader2 className='size-5 animate-spin text-slate-400' />
            </div>
          )}
        </div>
      </DataCenterLayout>
    </PageSkeleton>
  );
}
